import React from 'react';
import type { UnitDefinition, SeatingUnit } from '@/types/seating-v2.types';
import { UnitRenderer } from '../UnitRenderer';

interface UShapeArrangementProps {
  unit: UnitDefinition;
  units: SeatingUnit[];
  spacing: number;
  openSide?: 'top' | 'bottom' | 'left' | 'right';
  selectedSeat: number | null;
  onSeatClick: (globalSeatNumber: number) => void;
  onSeatDrop: (globalSeatNumber: number, guestId: number) => void;
}

/**
 * Arranges units in a U-shape (or hollow square).
 * Splits units into 3 segments: left arm, bottom row, right arm.
 */
export const UShapeArrangement: React.FC<UShapeArrangementProps> = ({
  unit,
  units,
  spacing,
  openSide = 'top',
  selectedSeat,
  onSeatClick,
  onSeatDrop,
}) => {
  if (units.length < 3) {
    // Too few units for U-shape, fall back to row
    return (
      <div className="flex items-center justify-center gap-2 p-6">
        {units.map(u => (
          <UnitRenderer
            key={u.unitIndex}
            unitIndex={u.unitIndex}
            label={u.label}
            unit={unit}
            seats={u.seats}
            selectedSeat={selectedSeat}
            onSeatClick={onSeatClick}
            onSeatDrop={onSeatDrop}
          />
        ))}
      </div>
    );
  }

  // Split units into 3 sections: left arm, bottom, right arm
  const total = units.length;
  const armSize = Math.floor(total / 3);
  const bottomSize = total - armSize * 2;

  const leftArm = units.slice(0, armSize);
  const bottom = units.slice(armSize, armSize + bottomSize);
  const rightArm = units.slice(armSize + bottomSize);

  // For openSide='top', the U opens upward:
  //   Left arm (vertical, going down)   |  empty top  |  Right arm (vertical, going down)
  //   ─────────────── Bottom row (horizontal) ───────────────

  return (
    <div className="flex flex-col items-center p-6" style={{ gap: spacing }}>
      {/* Stage / presentation area marker */}
      {openSide === 'top' && (
        <div className="w-2/3 max-w-sm py-2 bg-amber-50 border-2 border-amber-200 rounded-lg text-center text-amber-700 font-semibold text-sm shadow-sm">
          🎤 PRESENTATION AREA
        </div>
      )}

      {/* Top section: left arm + gap + right arm */}
      <div className="flex items-start" style={{ gap: spacing * 2 }}>
        {/* Left arm — vertical column */}
        <div className="flex flex-col" style={{ gap: spacing }}>
          {leftArm.map(u => (
            <UnitRenderer
              key={u.unitIndex}
              unitIndex={u.unitIndex}
              label={u.label}
              unit={unit}
              seats={u.seats}
              selectedSeat={selectedSeat}
              onSeatClick={onSeatClick}
              onSeatDrop={onSeatDrop}
            />
          ))}
        </div>

        {/* Right arm — vertical column */}
        <div className="flex flex-col" style={{ gap: spacing }}>
          {rightArm.map(u => (
            <UnitRenderer
              key={u.unitIndex}
              unitIndex={u.unitIndex}
              label={u.label}
              unit={unit}
              seats={u.seats}
              selectedSeat={selectedSeat}
              onSeatClick={onSeatClick}
              onSeatDrop={onSeatDrop}
            />
          ))}
        </div>
      </div>

      {/* Bottom row — horizontal */}
      <div className="flex items-center justify-center" style={{ gap: spacing }}>
        {bottom.map(u => (
          <UnitRenderer
            key={u.unitIndex}
            unitIndex={u.unitIndex}
            label={u.label}
            unit={unit}
            seats={u.seats}
            selectedSeat={selectedSeat}
            onSeatClick={onSeatClick}
            onSeatDrop={onSeatDrop}
          />
        ))}
      </div>
    </div>
  );
};

export default UShapeArrangement;
